import formatName from 'I18n/utils/formatName';
import invariant from 'react-utils/invariant';
import { getContactRecordUrl, getCompanyRecordUrl, getDealRecordUrl, getTicketRecordUrl, getConversationRecordUrl, getQuoteRecordUrl, getCustomObjectRecordUrl, getUserUrl } from 'flow-doctor-lib/utils/urls';
export const isHubSpotDefinedObject = objectTypeId => objectTypeId.startsWith('0');
export const isIntegratorDefinedObject = objectTypeId => objectTypeId.startsWith('1');
export const isCustomObject = objectTypeId => objectTypeId.startsWith('2');
export const isHubSpotDefinedEvent = objectTypeId => objectTypeId.startsWith('4');
export const isIntegratorDefinedEvent = objectTypeId => objectTypeId.startsWith('5');
export const isPortalDefinedEvent = objectTypeId => objectTypeId.startsWith('6');
export const isObjectType = objectTypeId => isHubSpotDefinedObject(objectTypeId) || isIntegratorDefinedObject(objectTypeId) || isCustomObject(objectTypeId);
export const isEventType = objectTypeId => isHubSpotDefinedEvent(objectTypeId) || isIntegratorDefinedEvent(objectTypeId) || isPortalDefinedEvent(objectTypeId);
export const getUrlForObjectTypeId = ({
  objectTypeId,
  portalId,
  objectId,
  threadId,
  userId
}) => {
  // Custom objects
  if (isCustomObject(objectTypeId)) {
    return getCustomObjectRecordUrl({
      objectTypeId,
      portalId,
      objectId
    });
  }
  switch (objectTypeId) {
    case '0-1':
      return getContactRecordUrl({
        portalId,
        objectId
      });
    case '0-2':
      return getCompanyRecordUrl({
        portalId,
        objectId
      });
    case '0-3':
      return getDealRecordUrl({
        portalId,
        objectId
      });
    case '0-5':
      return getTicketRecordUrl({
        portalId,
        objectId
      });
    case '0-11':
      return threadId ? getConversationRecordUrl({
        portalId,
        threadId
      }) : null;
    case '0-14':
      return getQuoteRecordUrl({
        portalId,
        objectId
      });
    case '0-115':
      return userId ? getUserUrl({
        portalId,
        userId
      }) : null;
    default:
      // Unknown object, just return null
      return null;
  }
};

// Implements logic similar to https://git.hubteam.com/HubSpot/reference-resolvers/blob/58a031f8e8003ba1aed97857f6204eb313e293e8/static/js/formatters/formatContacts.js#L21
const getIdentifierForContact = crmObject => {
  invariant(crmObject.objectTypeId === '0-1');
  const firstNameProperty = crmObject.properties.firstname;
  const lastNameProperty = crmObject.properties.lastname;
  const emailProperty = crmObject.properties.email;
  if (firstNameProperty && lastNameProperty) {
    return formatName({
      firstName: firstNameProperty.value,
      lastName: lastNameProperty.value
    });
  }
  if (firstNameProperty) {
    return firstNameProperty.value;
  }
  if (emailProperty) {
    return emailProperty.value;
  }

  // Ideally should never happen, but not sure if all three fields above can be omitted
  return crmObject.objectId;
};
export const getIdentifiersForContactProfile = crmObject => {
  invariant(crmObject.objectTypeId === '0-1');
  const emailProperty = crmObject.properties.email;
  const email = emailProperty ? emailProperty.value : null;
  return {
    primary: getIdentifierForContact(crmObject),
    secondary: email
  };
};
const getIdentifierForObject = (crmObject, propertyName) => {
  const identifierProperty = crmObject.properties[propertyName];
  return identifierProperty ? identifierProperty.value : crmObject.objectId;
};
const getIdentifierForEngagement = crmObject => {
  const {
    properties
  } = crmObject;
  const engagementType = properties.hs_engagement_type;
  switch (engagementType.value) {
    case 'CALL':
      return getIdentifierForObject(crmObject, 'hs_call_title');
    case 'MEETING':
      return getIdentifierForObject(crmObject, 'hs_meeting_title');
    case 'PUBLISHING_TASK':
      return getIdentifierForObject(crmObject, 'hs_publishing_task_name');
    case 'SMS':
      return getIdentifierForObject(crmObject, 'hs_name');
    case 'TASK':
      return getIdentifierForObject(crmObject, 'hs_task_subject');
    case 'CONVERSATION_SESSION':
    case 'INCOMING_EMAIL':
    case 'EMAIL':
    case 'FORWARDED_EMAIL':
    case 'LINKEDIN_MESSAGE':
    case 'NOTE':
    case 'POSTAL_MAIL':
    case 'WHATS_APP':
    default:
      return null;
  }
};
export const getIdentifierPropertyForObject = (crmObject, objectDefinition) => {
  if (isCustomObject(crmObject.objectTypeId) && objectDefinition && objectDefinition.primaryDisplayLabelPropertyName) {
    return getIdentifierForObject(crmObject, objectDefinition.primaryDisplayLabelPropertyName);
  }
  switch (crmObject.objectTypeId) {
    case '0-1':
      return getIdentifierForContact(crmObject);
    case '0-2':
      return getIdentifierForObject(crmObject, 'name');
    case '0-3':
      return getIdentifierForObject(crmObject, 'dealname');
    case '0-4':
      return getIdentifierForEngagement(crmObject);
    case '0-5':
      return getIdentifierForObject(crmObject, 'subject');
    case '0-11':
      return getIdentifierForObject(crmObject, 'hs_thread_id');
    case '0-14':
      return getIdentifierForObject(crmObject, 'hs_title');
    default:
      {
        if (objectDefinition && objectDefinition.primaryDisplayLabelPropertyName) {
          return getIdentifierForObject(crmObject, objectDefinition.primaryDisplayLabelPropertyName);
        }
        return crmObject.secondaryIdentifier;
      }
  }
};
export const getPropertyForObject = (object, property) => {
  var _object$properties$pr, _object$properties$pr2;
  return (_object$properties$pr = (_object$properties$pr2 = object.properties[property]) === null || _object$properties$pr2 === void 0 ? void 0 : _object$properties$pr2.value) !== null && _object$properties$pr !== void 0 ? _object$properties$pr : null;
};